import { observable } from 'mobx';
import type { InfomartApiTokenArgs } from './infomart_api_token';
import InfomartApiToken from './infomart_api_token';
import { OrganizationSystemFeeSetting } from './organization_system_fee_setting';
import type { OrganizationSystemFeeSettingArgs } from './organization_system_fee_setting';
import type { ShinkansenLimitTypeJson } from './shinkansen_limit_type';
import ShinkansenLimitType from './shinkansen_limit_type';
import type { HotelLimitTypeJson } from './hotel_limit_type';
import HotelLimitType from './hotel_limit_type';
import type { DomesticAirLimitTypeJson } from './domestic_air_limit_type';
import DomesticAirLimitType from './domestic_air_limit_type';
import type { RentalCarLimitTypeJson } from './rental_car_limit_type';
import RentalCarLimitType from './rental_car_limit_type';
import type { PackageLimitTypeJson } from './package_limit_type';
import PackageLimitType from './package_limit_type';
import type { RailwayPackageLimitTypeJson } from '../railway_package_limit_type/railway_package_limit_type';
import RailwayPackageLimitType from '../railway_package_limit_type/railway_package_limit_type';
import type { ArrangementRequestLimitTypeJson } from './arrangement_request_limit_type';
import ArrangementRequestLimitType from './arrangement_request_limit_type';
import type { ArrangementRequestSettingJson } from './arrangement_request_setting2';
import ArrangementRequestSetting from './arrangement_request_setting2';
import type { InvoiceSettingArgs } from './invoice_setting';
import InvoiceSetting from './invoice_setting';
import type { SettingResponseArgs } from '../setting2';
import Setting, { convertSettingResponseToArgs } from '../setting2';
import type { GradeResponseArgs } from '../grade2';
import Grade, { convertGradeResponseToArgs } from '../grade2';

export const RevenueReferenceDateTypes: { [key: string]: string } = {
  confirmed_at: '予約確定日',
  end_date: '出張完了日'
};

export type WorkflowStyle = 'department' | 'trip';
export type PaymentMethodOptions = 'invoice' | 'card' | 'selectable';
export type PaymentMethodType = 'billing' | 'card';
export type PaymentGatewayType = 'gmo' | 'sony';
export type WorkflowType = 'selected_amount' | 'with_buffer' | 'price_limit';
export type ReserveRequestMailNotificationType = 'togglable' | 'selectable_user';

export type ForbiddenRepositoryArgs = {
  category: string;
  repository: string;
};

type InvoiceSplitType = 'none' | 'charging_department' | 'project' | 'invoice_split_target';
type TransitSearchApi = 'legacy' | 'ekispert';
type TransportElementReservationApi = 'smart_ticket' | 'ticket_opr';
type TripReportWorkflowStyle = 'department' | 'project' | 'trip_report';

export type OrganizationAdmin = {
  name: string;
  email: string;
};

export type OrganizationResponseArgs = {
  id: number;
  name?: string;
  url?: string;
  address?: string;
  tel?: string;
  revenue_reference_date_type?: number;
  setting?: SettingResponseArgs;
  freee_workflow?: boolean;
  exic_usage_mode?: string;
  allow_user_exic_input?: boolean;
  show_ex?: boolean;
  ai_reservation_enabled?: boolean;
  show_fee?: boolean;
  workflow_style?: WorkflowStyle;
  concur_client_id?: string;
  concur_client_secret?: string;
  forbidden_repositories?: ForbiddenRepositoryArgs[]; // 右ナビのSMAS利用の表示に使う
  invoice_split_type?: InvoiceSplitType;
  amadeus_company_name?: string;
  person_in_charge_name?: string;
  person_in_charge_email?: string;
  billing_email?: string;
  bank_account_info?: string;
  saving_account_id?: number;
  allow_overdraft?: boolean;
  service_id: number | string;
  infomart_private_cust_cd?: string;
  infomart_transfer_code?: string;
  infomart_api_token?: InfomartApiTokenArgs;
  salesforce_id?: string;
  salesforce_import_id?: string;
  enterprise_identification_number?: string;
  segment1?: string;
  segment2?: string;
  company_size?: string;
  user_dashboard_label1?: string;
  user_dashboard_label2?: string;
  user_dashboard_label3?: string;
  member_count?: number;
  admin_count?: number;
  is_own_company?: boolean;
  use_chatbot?: boolean;
  system_fee_setting?: OrganizationSystemFeeSettingArgs;
  payment_method_type?: PaymentMethodOptions;
  payment_gateway_type?: PaymentGatewayType;
  transit_search_api?: TransitSearchApi;
  ekispert_display_limited?: boolean;
  transport_element_reservation_api?: TransportElementReservationApi;
  include_tax?: boolean;
  hotel_sort_type?: boolean;
  enable_auto_reserve_rakuten_hotel?: boolean;
  enable_ticket_opr_auto_ticketing?: boolean;
  use_kyuusyuu_ticket?: boolean;
  enable_rentalcar_form?: boolean;
  day_off_operation?: boolean;
  shinkansen_limit_type?: ShinkansenLimitTypeJson;
  hotel_limit_type?: HotelLimitTypeJson;
  domestic_air_limit_type?: DomesticAirLimitTypeJson;
  rental_car_limit_type?: RentalCarLimitTypeJson;
  package_limit_type?: PackageLimitTypeJson;
  railway_package_limit_type?: RailwayPackageLimitTypeJson;
  arrangement_request_limit_type?: ArrangementRequestLimitTypeJson;
  use_qr?: boolean;
  use_postal?: boolean;
  exic_basic_member_no?: string;
  use_exic_reference_number?: boolean;
  use_non_order_item?: boolean;
  show_exic_seisan_trips?: boolean;
  allow_apply_past_date?: boolean;
  reserve_request_mail_notification_type?: ReserveRequestMailNotificationType;
  use_smart_search?: boolean;
  rakuten_inner_fee_rate?: number;
  exic_agency_fee_rate?: number;
  business_ekinet_corporate_inquiry_number?: string;
  business_ekinet_agency_fee_rate?: number;
  trip_report_workflow_style?: TripReportWorkflowStyle;
  enable_auto_stock_check?: boolean;
  enable_batch_approval?: boolean;
  arrangement_request_setting?: ArrangementRequestSettingJson;
  request_form_approval_required?: boolean;
  immigration_card_enabled?: boolean;
  visa_application_enabled?: boolean;
  invoice_setting?: InvoiceSettingArgs;
  domestic_hotel_under_regulation_workflow_type: WorkflowType;
  domestic_hotel_under_regulation_buffer_amount: number;
  domestic_hotel_over_regulation_workflow_type: WorkflowType;
  domestic_hotel_over_regulation_buffer_amount: number;
  foreign_hotel_under_regulation_workflow_type: WorkflowType;
  foreign_hotel_under_regulation_buffer_amount: number;
  foreign_hotel_over_regulation_workflow_type: WorkflowType;
  foreign_hotel_over_regulation_buffer_amount: number;
  domestic_flight_workflow_type: WorkflowType;
  domestic_flight_buffer_amount: number;
  foreign_flight_workflow_type: WorkflowType;
  foreign_flight_buffer_amount: number;
  package_workflow_type: WorkflowType;
  package_buffer_amount: number;
  grades?: GradeResponseArgs[];
  admins?: OrganizationAdmin[]; // reserve_confirmで費用負担部署の承認者が設定されていない場合に管理者名を表示する用途
  enabled_customize_address?: boolean;
};

export type OrganizationArgs = {
  id?: number;
  name?: string;
  url?: string;
  address?: string;
  tel?: string;
  revenueReferenceDateType?: number;
  setting?: Setting;
  freeeWorkflow?: boolean;
  exicUsageMode?: string;
  allowUserExicInput?: boolean;
  showEx?: boolean;
  aiReservationEnabled?: boolean;
  showFee?: boolean;
  workflowStyle?: WorkflowStyle;
  concurClientId?: string;
  concurClientSecret?: string;
  forbiddenRepositories?: ForbiddenRepositoryArgs[]; // 右ナビのSMAS利用の表示に使う
  invoiceSplitType?: InvoiceSplitType;
  amadeusCompanyName?: string;
  personInChargeName?: string;
  personInChargeEmail?: string;
  billingEmail?: string;
  bankAccountInfo?: string;
  savingAccountId?: number;
  allowOverdraft?: boolean;
  serviceId: number | string;
  infomartPrivateCustCd?: string;
  infomartTransferCode?: string;
  infomartApiToken?: InfomartApiToken;
  salesforceId?: string;
  salesforceImportId?: string;
  enterpriseIdentificationNumber?: string;
  segment1?: string;
  segment2?: string;
  companySize?: string;
  userDashboardLabel1?: string;
  userDashboardLabel2?: string;
  userDashboardLabel3?: string;
  memberCount?: number;
  adminCount?: number;
  isOwnCompany?: boolean;
  useChatbot?: boolean;
  systemFeeSetting?: OrganizationSystemFeeSetting;
  paymentMethodType?: PaymentMethodOptions;
  paymentGatewayType?: PaymentGatewayType;
  transitSearchApi?: TransitSearchApi;
  ekispertDisplayLimited?: boolean;
  transportElementReservationApi?: TransportElementReservationApi;
  includeTax?: boolean;
  hotelSortType?: boolean;
  enableAutoReserveRakutenHotel?: boolean;
  enableTicketOprAutoTicketing?: boolean;
  useKyuusyuuTicket?: boolean;
  enableRentalcarForm?: boolean;
  dayOffOperation?: boolean;
  shinkansenLimitType?: ShinkansenLimitType;
  hotelLimitType?: HotelLimitType;
  domesticAirLimitType?: DomesticAirLimitType;
  rentalCarLimitType?: RentalCarLimitType;
  packageLimitType?: PackageLimitType;
  railwayPackageLimitType?: RailwayPackageLimitType;
  arrangementRequestLimitType?: ArrangementRequestLimitType;
  useQr?: boolean;
  usePostal?: boolean;
  exicBasicMemberNo?: string;
  useExicReferenceNumber?: boolean;
  useNonOrderItem?: boolean;
  showExicSeisanTrips?: boolean;
  allowApplyPastDate?: boolean;
  reserveRequestMailNotificationType?: ReserveRequestMailNotificationType;
  useSmartSearch?: boolean;
  rakutenInnerFeeRate?: number;
  exicAgencyFeeRate?: number;
  businessEkinetCorporateInquiryNumber?: string;
  businessEkinetAgencyFeeRate?: number;
  tripReportWorkflowStyle?: TripReportWorkflowStyle;
  enableAutoStockCheck?: boolean;
  enableBatchApproval?: boolean;
  arrangementRequestSetting?: ArrangementRequestSetting;
  requestFormApprovalRequired?: boolean;
  immigrationCardEnabled?: boolean;
  visaApplicationEnabled?: boolean;
  invoiceSetting?: InvoiceSetting;
  domesticHotelUnderRegulationWorkflowType?: WorkflowType;
  domesticHotelUnderRegulationBufferAmount?: number;
  domesticHotelOverRegulationWorkflowType?: WorkflowType;
  domesticHotelOverRegulationBufferAmount?: number;
  foreignHotelUnderRegulationWorkflowType?: WorkflowType;
  foreignHotelUnderRegulationBufferAmount?: number;
  foreignHotelOverRegulationWorkflowType?: WorkflowType;
  foreignHotelOverRegulationBufferAmount?: number;
  domesticFlightWorkflowType?: WorkflowType;
  domesticFlightBufferAmount?: number;
  foreignFlightWorkflowType?: WorkflowType;
  foreignFlightBufferAmount?: number;
  packageWorkflowType?: WorkflowType;
  packageBufferAmount?: number;
  grades?: Grade[];
  admins?: OrganizationAdmin[];
  enabledCustomizeAddress?: boolean;
};

export const convertOrganizationResponseToArgs = (response: OrganizationResponseArgs) => {
  const domesticAirLimitTypeForService = () => {
    if (!utils.isMynavi(response.service_id)) return {};
    return { business_days: 1, hour: 12 };
  };
  const packageLimitTypeForService = () => {
    if (!utils.isMynavi(response.service_id)) return {};
    return { business_days: 7, hour: 0 };
  };

  const args: OrganizationArgs = {
    id: response.id,
    name: response.name,
    url: response.url,
    address: response.address,
    tel: response.tel,
    revenueReferenceDateType: response.revenue_reference_date_type,
    setting: response.setting ? new Setting(convertSettingResponseToArgs(response.setting)) : undefined,
    freeeWorkflow: response.freee_workflow,
    exicUsageMode: response.exic_usage_mode,
    allowUserExicInput: response.allow_user_exic_input,
    showEx: response.show_ex,
    aiReservationEnabled: response.ai_reservation_enabled,
    showFee: response.show_fee,
    workflowStyle: response.workflow_style,
    concurClientId: response.concur_client_id,
    concurClientSecret: response.concur_client_secret,
    forbiddenRepositories: response.forbidden_repositories,
    invoiceSplitType: response.invoice_split_type,
    amadeusCompanyName: response.amadeus_company_name,
    personInChargeName: response.person_in_charge_name,
    personInChargeEmail: response.person_in_charge_email,
    billingEmail: response.billing_email,
    bankAccountInfo: response.bank_account_info,
    savingAccountId: response.saving_account_id,
    allowOverdraft: response.allow_overdraft,
    serviceId: response.service_id,
    infomartPrivateCustCd: response.infomart_private_cust_cd,
    infomartTransferCode: response.infomart_transfer_code,
    infomartApiToken: new InfomartApiToken(response.infomart_api_token || ({} as InfomartApiTokenArgs)),
    salesforceId: response.salesforce_id,
    salesforceImportId: response.salesforce_import_id,
    enterpriseIdentificationNumber: response.enterprise_identification_number,
    segment1: response.segment1,
    segment2: response.segment2,
    companySize: response.company_size,
    userDashboardLabel1: response.user_dashboard_label1,
    userDashboardLabel2: response.user_dashboard_label2,
    userDashboardLabel3: response.user_dashboard_label3,
    memberCount: response.member_count,
    adminCount: response.admin_count,
    isOwnCompany: response.is_own_company,
    useChatbot: response.use_chatbot,
    systemFeeSetting: new OrganizationSystemFeeSetting(
      response.system_fee_setting || ({} as OrganizationSystemFeeSettingArgs)
    ),
    paymentMethodType: response.payment_method_type,
    paymentGatewayType: response.payment_gateway_type,
    transitSearchApi: response.transit_search_api,
    ekispertDisplayLimited: response.ekispert_display_limited,
    transportElementReservationApi: response.transport_element_reservation_api,
    includeTax: response.include_tax,
    hotelSortType: response.hotel_sort_type,
    enableAutoReserveRakutenHotel: response.enable_auto_reserve_rakuten_hotel,
    enableTicketOprAutoTicketing: response.enable_ticket_opr_auto_ticketing,
    useKyuusyuuTicket: response.use_kyuusyuu_ticket,
    enableRentalcarForm: response.enable_rentalcar_form,
    dayOffOperation: response.day_off_operation,
    shinkansenLimitType: new ShinkansenLimitType(
      response.shinkansen_limit_type || ({} as ShinkansenLimitTypeJson)
    ),
    hotelLimitType: response.hotel_limit_type
      ? new HotelLimitType(response.hotel_limit_type)
      : HotelLimitType.newForService(response.service_id),
    domesticAirLimitType: new DomesticAirLimitType(
      response.domestic_air_limit_type || domesticAirLimitTypeForService()
    ),
    rentalCarLimitType: new RentalCarLimitType(response.rental_car_limit_type || ({} as RentalCarLimitTypeJson)),
    packageLimitType: new PackageLimitType(response.package_limit_type || packageLimitTypeForService()),
    railwayPackageLimitType: new RailwayPackageLimitType(response.railway_package_limit_type || {}),
    arrangementRequestLimitType: new ArrangementRequestLimitType(response.arrangement_request_limit_type || {}),
    useQr: response.use_qr,
    usePostal: response.use_postal,
    exicBasicMemberNo: response.exic_basic_member_no,
    useExicReferenceNumber: response.use_exic_reference_number,
    useNonOrderItem: response.use_non_order_item,
    showExicSeisanTrips: response.show_exic_seisan_trips,
    allowApplyPastDate: response.allow_apply_past_date,
    reserveRequestMailNotificationType: response.reserve_request_mail_notification_type,
    useSmartSearch: response.use_smart_search,
    rakutenInnerFeeRate: response.rakuten_inner_fee_rate,
    exicAgencyFeeRate: response.exic_agency_fee_rate,
    businessEkinetCorporateInquiryNumber: response.business_ekinet_corporate_inquiry_number,
    businessEkinetAgencyFeeRate: response.business_ekinet_agency_fee_rate,
    tripReportWorkflowStyle: response.trip_report_workflow_style,
    enableAutoStockCheck: response.enable_auto_stock_check,
    enableBatchApproval: response.enable_batch_approval,
    arrangementRequestSetting: new ArrangementRequestSetting(
      response.arrangement_request_setting || ({} as ArrangementRequestSettingJson)
    ),
    requestFormApprovalRequired: response.request_form_approval_required,
    immigrationCardEnabled: response.immigration_card_enabled,
    visaApplicationEnabled: response.visa_application_enabled,
    invoiceSetting: new InvoiceSetting(response.invoice_setting || ({} as InvoiceSettingArgs)),
    domesticHotelUnderRegulationWorkflowType: response.domestic_hotel_under_regulation_workflow_type,
    domesticHotelUnderRegulationBufferAmount: response.domestic_hotel_under_regulation_buffer_amount,
    domesticHotelOverRegulationWorkflowType: response.domestic_hotel_over_regulation_workflow_type,
    domesticHotelOverRegulationBufferAmount: response.domestic_hotel_over_regulation_buffer_amount,
    foreignHotelUnderRegulationWorkflowType: response.foreign_hotel_under_regulation_workflow_type,
    foreignHotelUnderRegulationBufferAmount: response.foreign_hotel_under_regulation_buffer_amount,
    foreignHotelOverRegulationWorkflowType: response.foreign_hotel_over_regulation_workflow_type,
    foreignHotelOverRegulationBufferAmount: response.foreign_hotel_over_regulation_buffer_amount,
    domesticFlightWorkflowType: response.domestic_flight_workflow_type,
    domesticFlightBufferAmount: response.domestic_flight_buffer_amount,
    foreignFlightWorkflowType: response.foreign_flight_workflow_type,
    foreignFlightBufferAmount: response.foreign_flight_buffer_amount,
    packageWorkflowType: response.package_workflow_type,
    packageBufferAmount: response.package_buffer_amount,
    grades: response.grades?.map(grade => new Grade(convertGradeResponseToArgs(grade))) || [],
    admins: response.admins,
    enabledCustomizeAddress: response.enabled_customize_address
  };
  return args;
};

export default class Organization {
  @observable id?: number;
  @observable name?: string;
  @observable url?: string;
  @observable address?: string;
  @observable tel?: string;
  @observable revenueReferenceDateType?: number;
  @observable setting?: Setting;
  @observable freeeWorkflow?: boolean;
  @observable exicUsageMode?: string;
  @observable allowUserExicInput?: boolean;
  @observable showEx?: boolean;
  @observable aiReservationEnabled?: boolean;
  @observable showFee?: boolean;
  @observable workflowStyle?: WorkflowStyle;
  @observable concurClientId: string | null | undefined;
  @observable concurClientSecret: string | null | undefined;
  @observable forbiddenRepositories?: ForbiddenRepositoryArgs[];
  @observable invoiceSplitType?: InvoiceSplitType | null;
  @observable amadeusCompanyName?: string | null;
  @observable personInChargeName?: string | null;
  @observable personInChargeEmail?: string | null;
  @observable billingEmail?: string | null;
  @observable bankAccountInfo?: string | null;
  @observable savingAccountId?: number | null;
  @observable allowOverdraft?: boolean;
  @observable serviceId: number | string;
  @observable infomartPrivateCustCd?: string | null;
  @observable infomartTransferCode?: string | null;
  @observable infomartApiToken?: InfomartApiToken;
  @observable salesforceId?: string | null;
  @observable salesforceImportId?: string | null;
  @observable enterpriseIdentificationNumber?: string | null;
  @observable segment1?: string | null;
  @observable segment2?: string | null;
  @observable companySize?: string | null;
  @observable userDashboardLabel1?: string | null;
  @observable userDashboardLabel2?: string | null;
  @observable userDashboardLabel3?: string | null;
  @observable memberCount?: number | null;
  @observable adminCount?: number | null;
  @observable isOwnCompany?: boolean;
  @observable useChatbot?: boolean;
  @observable systemFeeSetting?: OrganizationSystemFeeSetting;
  @observable paymentMethodType?: PaymentMethodOptions;
  @observable paymentGatewayType?: PaymentGatewayType;
  @observable transitSearchApi?: TransitSearchApi;
  @observable ekispertDisplayLimited?: boolean;
  @observable transportElementReservationApi?: TransportElementReservationApi;
  @observable includeTax?: boolean;
  @observable hotelSortType?: boolean;
  @observable enableAutoReserveRakutenHotel?: boolean;
  @observable enableTicketOprAutoTicketing?: boolean;
  @observable useKyuusyuuTicket?: boolean;
  @observable enableRentalcarForm?: boolean;
  @observable dayOffOperation?: boolean;
  @observable shinkansenLimitType?: ShinkansenLimitType;
  @observable hotelLimitType?: HotelLimitType;
  @observable domesticAirLimitType?: DomesticAirLimitType;
  @observable rentalCarLimitType?: RentalCarLimitType;
  @observable packageLimitType?: PackageLimitType;
  @observable railwayPackageLimitType?: RailwayPackageLimitType;
  @observable arrangementRequestLimitType?: ArrangementRequestLimitType;
  @observable useQr?: boolean;
  @observable usePostal?: boolean;
  @observable exicBasicMemberNo?: string | null;
  @observable useExicReferenceNumber?: boolean;
  @observable useNonOrderItem?: boolean;
  @observable showExicSeisanTrips?: boolean;
  @observable allowApplyPastDate?: boolean;
  @observable reserveRequestMailNotificationType?: ReserveRequestMailNotificationType;
  @observable useSmartSearch?: boolean;
  @observable rakutenInnerFeeRate?: number | null;
  @observable exicAgencyFeeRate?: number | null;
  @observable businessEkinetCorporateInquiryNumber?: string | null;
  @observable businessEkinetAgencyFeeRate?: number | null;
  @observable tripReportWorkflowStyle?: TripReportWorkflowStyle;
  @observable enableAutoStockCheck?: boolean;
  @observable enableBatchApproval?: boolean;
  @observable arrangementRequestSetting?: ArrangementRequestSetting;
  @observable requestFormApprovalRequired?: boolean;
  @observable immigrationCardEnabled?: boolean;
  @observable visaApplicationEnabled?: boolean;
  @observable invoiceSetting?: InvoiceSetting;
  @observable domesticHotelUnderRegulationWorkflowType: WorkflowType;
  @observable domesticHotelUnderRegulationBufferAmount: number;
  @observable domesticHotelOverRegulationWorkflowType: WorkflowType;
  @observable domesticHotelOverRegulationBufferAmount: number;
  @observable foreignHotelUnderRegulationWorkflowType: WorkflowType;
  @observable foreignHotelUnderRegulationBufferAmount: number;
  @observable foreignHotelOverRegulationWorkflowType?: WorkflowType;
  @observable foreignHotelOverRegulationBufferAmount: number;
  @observable domesticFlightWorkflowType: WorkflowType;
  @observable domesticFlightBufferAmount: number;
  @observable foreignFlightWorkflowType: WorkflowType;
  @observable foreignFlightBufferAmount: number;
  @observable packageWorkflowType: WorkflowType;
  @observable packageBufferAmount: number;
  @observable grades: Grade[];
  @observable admins?: OrganizationAdmin[];
  @observable enabledCustomizeAddress?: boolean;

  constructor(args: OrganizationArgs) {
    this.id = args.id;
    this.name = args.name;
    this.url = args.url;
    this.address = args.address;
    this.tel = args.tel;
    this.revenueReferenceDateType = args.revenueReferenceDateType;
    this.setting = args.setting;
    this.freeeWorkflow = args.freeeWorkflow;
    this.exicUsageMode = args.exicUsageMode;
    this.allowUserExicInput = args.allowUserExicInput;
    this.showEx = args.showEx;
    this.aiReservationEnabled = args.aiReservationEnabled;
    this.showFee = args.showFee;
    this.workflowStyle = args.workflowStyle;
    this.concurClientId = args.concurClientId;
    this.concurClientSecret = args.concurClientSecret;
    this.forbiddenRepositories = args.forbiddenRepositories;
    this.invoiceSplitType = args.invoiceSplitType;
    this.amadeusCompanyName = args.amadeusCompanyName;
    this.personInChargeName = args.personInChargeName;
    this.personInChargeEmail = args.personInChargeEmail;
    this.billingEmail = args.billingEmail;
    this.bankAccountInfo = args.bankAccountInfo;
    this.savingAccountId = args.savingAccountId;
    this.allowOverdraft = args.allowOverdraft;
    this.serviceId = args.serviceId;
    this.infomartPrivateCustCd = args.infomartPrivateCustCd;
    this.infomartTransferCode = args.infomartTransferCode;
    this.infomartApiToken = args.infomartApiToken;
    this.salesforceId = args.salesforceId;
    this.salesforceImportId = args.salesforceImportId;
    this.enterpriseIdentificationNumber = args.enterpriseIdentificationNumber;
    this.segment1 = args.segment1;
    this.segment2 = args.segment2;
    this.companySize = args.companySize;
    this.userDashboardLabel1 = args.userDashboardLabel1;
    this.userDashboardLabel2 = args.userDashboardLabel2;
    this.userDashboardLabel3 = args.userDashboardLabel3;
    this.memberCount = args.memberCount;
    this.adminCount = args.adminCount;
    this.isOwnCompany = args.isOwnCompany;
    this.useChatbot = args.useChatbot;
    this.systemFeeSetting = args.systemFeeSetting;
    this.paymentMethodType = args.paymentMethodType;
    this.paymentGatewayType = args.paymentGatewayType;
    this.transitSearchApi = args.transitSearchApi;
    this.ekispertDisplayLimited = args.ekispertDisplayLimited;
    this.transportElementReservationApi = args.transportElementReservationApi;
    this.includeTax = args.includeTax;
    this.hotelSortType = args.hotelSortType;
    this.enableAutoReserveRakutenHotel = args.enableAutoReserveRakutenHotel;
    this.enableTicketOprAutoTicketing = args.enableTicketOprAutoTicketing;
    this.useKyuusyuuTicket = args.useKyuusyuuTicket;
    this.enableRentalcarForm = args.enableRentalcarForm;
    this.dayOffOperation = args.dayOffOperation;
    this.shinkansenLimitType = args.shinkansenLimitType;
    this.hotelLimitType = args.hotelLimitType;
    this.domesticAirLimitType = args.domesticAirLimitType;
    this.rentalCarLimitType = args.rentalCarLimitType;
    this.packageLimitType = args.packageLimitType;
    this.railwayPackageLimitType = args.railwayPackageLimitType;
    this.arrangementRequestLimitType = args.arrangementRequestLimitType;
    this.useQr = args.useQr;
    this.usePostal = args.usePostal;
    this.exicBasicMemberNo = args.exicBasicMemberNo;
    this.useExicReferenceNumber = args.useExicReferenceNumber;
    this.useNonOrderItem = args.useNonOrderItem;
    this.showExicSeisanTrips = args.showExicSeisanTrips;
    this.allowApplyPastDate = args.allowApplyPastDate;
    this.reserveRequestMailNotificationType = args.reserveRequestMailNotificationType;
    this.useSmartSearch = args.useSmartSearch;
    this.rakutenInnerFeeRate = args.rakutenInnerFeeRate;
    this.exicAgencyFeeRate = args.exicAgencyFeeRate;
    this.businessEkinetCorporateInquiryNumber = args.businessEkinetCorporateInquiryNumber;
    this.businessEkinetAgencyFeeRate = args.businessEkinetAgencyFeeRate;
    this.tripReportWorkflowStyle = args.tripReportWorkflowStyle;
    this.enableAutoStockCheck = args.enableAutoStockCheck;
    this.enableBatchApproval = args.enableBatchApproval;
    this.arrangementRequestSetting = args.arrangementRequestSetting;
    this.requestFormApprovalRequired = args.requestFormApprovalRequired;
    this.immigrationCardEnabled = args.immigrationCardEnabled;
    this.visaApplicationEnabled = args.visaApplicationEnabled;
    this.invoiceSetting = args.invoiceSetting;
    this.paymentMethodType = args.paymentMethodType;
    this.domesticHotelUnderRegulationWorkflowType =
      args.domesticHotelUnderRegulationWorkflowType || 'selected_amount';
    this.domesticHotelUnderRegulationBufferAmount = args.domesticHotelUnderRegulationBufferAmount || 0;
    this.domesticHotelOverRegulationWorkflowType =
      args.domesticHotelOverRegulationWorkflowType || 'selected_amount';
    this.domesticHotelOverRegulationBufferAmount = args.domesticHotelOverRegulationBufferAmount || 0;
    this.foreignHotelUnderRegulationWorkflowType =
      args.foreignHotelUnderRegulationWorkflowType || 'selected_amount';
    this.foreignHotelUnderRegulationBufferAmount = args.foreignHotelUnderRegulationBufferAmount || 0;
    this.foreignHotelOverRegulationWorkflowType = args.foreignHotelOverRegulationWorkflowType || 'selected_amount';
    this.foreignHotelOverRegulationBufferAmount = args.foreignHotelOverRegulationBufferAmount || 0;
    this.domesticFlightWorkflowType = args.domesticFlightWorkflowType || 'selected_amount';
    this.domesticFlightBufferAmount = args.domesticFlightBufferAmount || 0;
    this.foreignFlightWorkflowType = args.foreignFlightWorkflowType || 'selected_amount';
    this.foreignFlightBufferAmount = args.foreignFlightBufferAmount || 0;
    this.packageWorkflowType = args.packageWorkflowType || 'selected_amount';
    this.packageBufferAmount = args.packageBufferAmount || 0;
    this.grades = args.grades || [];
    this.admins = args.admins;
    this.enableAutoReserveRakutenHotel = args.enableAutoReserveRakutenHotel;
    this.useQr = args.useQr;
    this.reserveRequestMailNotificationType = args.reserveRequestMailNotificationType;
    this.immigrationCardEnabled = args.immigrationCardEnabled;
    this.visaApplicationEnabled = args.visaApplicationEnabled;
    this.enabledCustomizeAddress = args.enabledCustomizeAddress;
  }

  getDomesticHotelBufferAmount(isOverRegulation: boolean, hotelPriceLimit: number, price: number): number {
    if (isOverRegulation) {
      return this.domesticHotelOverRegulationWorkflowType === 'with_buffer'
        ? this.domesticHotelOverRegulationBufferAmount
        : 0;
    }

    return this.domesticHotelUnderRegulationWorkflowType === 'with_buffer'
      ? this.domesticHotelUnderRegulationBufferAmount
      : this.domesticHotelUnderRegulationWorkflowType === 'price_limit'
      ? Math.max(hotelPriceLimit - price, 0)
      : 0;
  }

  getForeignHotelBufferAmount(isOverRegulation: boolean, hotelPriceLimit: number, price: number): number {
    if (isOverRegulation) {
      return this.foreignHotelOverRegulationWorkflowType === 'with_buffer'
        ? this.foreignHotelOverRegulationBufferAmount
        : 0;
    }

    return this.foreignHotelUnderRegulationWorkflowType === 'with_buffer'
      ? this.foreignHotelUnderRegulationBufferAmount
      : this.foreignHotelUnderRegulationWorkflowType === 'price_limit'
      ? Math.max(hotelPriceLimit - price, 0)
      : 0;
  }

  getDomesticFlightBufferAmount(): number {
    return this.domesticFlightWorkflowType === 'with_buffer' ? this.domesticFlightBufferAmount : 0;
  }

  getForeignFlightBufferAmount(): number {
    return this.foreignFlightWorkflowType === 'with_buffer' ? this.foreignFlightBufferAmount : 0;
  }

  getPackageBufferAmount(): number {
    return this.packageWorkflowType === 'with_buffer' ? this.packageBufferAmount : 0;
  }
}
